import * as z from 'zod';

const tremendousProductSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  category: z.string().min(1),
  subcategory: z.string().optional(),
  disclosure: z.string().optional(),
  countries: z.array(z.string()),
  currencyCodes: z.array(z.string()).optional(),
  skus: z.array(
    z.object({
      min: z.number(),
      max: z.number(),
      currencyCodes: z.array(z.string()),
    })
  ).min(1),
  imageUrl: z.string().url().optional(),
});

export const getRewardValidationSchema = (isEditMode: boolean, hasInitialAttachment: boolean) => {
  return z.object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().min(1, 'Description is required'),
    amount: z.coerce.number().min(1, 'Karma amount must be greater than 0'),
    category: z.enum(['DIGITAL', 'PHISICAL', 'VOUCHER']),
    isAvailable: z.boolean(),
    providerName: z.enum(['TAALY', 'AMAZON', 'UPPER', 'YAMANI_RESTURANT', 'TREMENDOUS']),
    amazonCurrency: z.string().optional(),
    amazonAmount: z.coerce.number().optional(),
    tremendousProduct: tremendousProductSchema.optional(),
    tremendousCurrencyCode: z.string().optional(),
    tremendousDenomination: z.coerce.number().optional(),
    countryList: z.array(z.string()),
    expiresAt: z.string().optional(),
    mediaFiles: z.array(z.any()),
  }).superRefine((data, ctx) => {
    if (data.providerName === 'AMAZON') {
      if (!data.amazonCurrency || data.amazonCurrency.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['amazonCurrency'],
          message: 'Currency is required for Amazon',
        });
      }
      if (!data.amazonAmount || data.amazonAmount <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['amazonAmount'],
          message: 'Gift card amount must be greater than 0',
        });
      }
    }

    if (data.providerName === 'TREMENDOUS') {
      if (!data.tremendousProduct) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tremendousProduct'],
          message: 'Select a Tremendous product first',
        });
      }

      if (!data.tremendousCurrencyCode || data.tremendousCurrencyCode.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tremendousCurrencyCode'],
          message: 'Currency is required for Tremendous rewards',
        });
      }

      if (!data.countryList.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['countryList'],
          message: 'Choose at least one supported country for this Tremendous reward',
        });
      }

      if (data.tremendousProduct && data.tremendousCurrencyCode) {
        const matchingSkus =
          Array.isArray(data.tremendousProduct.currencyCodes) &&
          data.tremendousProduct.currencyCodes.includes(data.tremendousCurrencyCode)
            ? data.tremendousProduct.skus
            : data.tremendousProduct.skus.filter((sku) =>
                sku.currencyCodes.includes(data.tremendousCurrencyCode!)
              );

        if (!matchingSkus.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['tremendousCurrencyCode'],
            message: 'The selected product does not support that currency',
          });
        } else {
          const min = Math.min(...matchingSkus.map((sku) => sku.min));
          const max = Math.max(...matchingSkus.map((sku) => sku.max));

          if (!data.tremendousDenomination || data.tremendousDenomination < min || data.tremendousDenomination > max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['tremendousDenomination'],
              message: `Enter a denomination between ${min} and ${max}`,
            });
          }
        }

        const unsupportedCountries = data.countryList.filter(
          (country) => !data.tremendousProduct!.countries.includes(country)
        );

        if (unsupportedCountries.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['countryList'],
            message: 'One or more selected countries are not supported by the chosen Tremendous product',
          });
        }
      }
    }

    const requiresMediaUpload = data.providerName !== 'TREMENDOUS';

    if (requiresMediaUpload && !isEditMode && data.mediaFiles.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mediaFiles'],
        message: 'At least one media file is required',
      });
    } else if (requiresMediaUpload && isEditMode && !hasInitialAttachment && data.mediaFiles.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mediaFiles'],
        message: 'At least one media file is required',
      });
    }
  });
};

export type CreateRewardFormData = z.infer<ReturnType<typeof getRewardValidationSchema>>;
